import { defineRule } from "vee-validate";
import i18n from "@/i18n";
import dayjs from "@/plugins/dayjs";

/**
 * Services in charge of handling date validation rules.
 */
export class DateValidationService {
  static DATE_PATTERNS: Record<string, {
    pattern: string;
    regexp: RegExp;
    replace: string;
  }> = {
    "dd/MM/yyyy": {
      pattern: "DD-MM-YYYY",
      regexp: /(\d{2})\/(\d{2})\/(\d{4})/,
      replace: "$1 $2 $3",
    },
    "dd/MM/yy": {
      pattern: "DD-MM-YY",
      regexp: /(\d{2})\/(\d{2})\/(\d{2})/,
      replace: "$1 $2 $3",
    },
    "MM/yyyy": {
      pattern: "MM-YYYY",
      regexp: /(\d{2})\/(\d{4})/,
      replace: "$1 $2",
    },
    "MM/yy": {
      pattern: "MM-YY",
      regexp: /(\d{2})\/(\d{2})/,
      replace: "$1 $2",
    },
    "yyyy": {
      pattern: "YYYY",
      regexp: /(\d{4})/,
      replace: "$1",
    },
    "hh:mm": {
      pattern: "hh:mm",
      regexp: /(\d{2}):(\d{2})/,
      replace: "$1 $2",
    },
    "HH:mm:ss": {
      pattern: "HH:mm:ss",
      regexp: /(\d{2}):(\d{2}):(\d{2})/,
      replace: "$1 $2 $3",
    },
    "dd/MM/yyyy HH:mm:ss": {
      pattern: "DD-MM-YYYY HH:mm:ss",
      regexp: /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})/,
      replace: "$1 $2 $3 $4:$5:$6",
    },
    "dd/MM/yy HH:mm:ss": {
      pattern: "DD-MM-YY HH:mm:ss",
      regexp: /(\d{2})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})/,
      replace: "$1 $2 $3 $4:$5:$6",
    },
  };

  static validateDate(dateToValidate?: string | number | Date | null | undefined, pattern: string = "dd/MM/yyyy"): boolean {
    const patternElement = DateValidationService.DATE_PATTERNS[pattern];
    if (!patternElement || !dateToValidate) return false;
    const parsed = dayjs(dateToValidate as dayjs.ConfigType, patternElement.pattern, true);
    return parsed.isValid() && parsed.format(patternElement.pattern).replaceAll("-", "/") === dateToValidate;
  }

  static init(): void {
    // The date validation rules expects a date pattern parameter
    // eg. rules = "date|pattern:dd/MM/YYYY"
    // See application example in OreInputData
    defineRule("date", (value: string, params: string[]) => {
      const pattern = params[0];
      let hasValidDate = false;
      if (Array.isArray(value)) {
        hasValidDate = value.every(date => DateValidationService.validateDate(date, pattern));
      }
      else {
        hasValidDate = DateValidationService.validateDate(value, pattern);
      }
      if (hasValidDate) {
        return true;
      }
      else {
        return i18n.global.t("rules.date", { pattern: pattern });
      }
    });
  }
}
